import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DeleteOutline,
  Edit,
  ReceiptLong,
  UploadFile,
  Visibility,
} from '@mui/icons-material';
import PaymentAttachmentPreview from './PaymentAttachmentPreview';
import PaymentStatusChip from './PaymentStatusChip';
import { formatCurrency, formatDate } from '../paymentNormalizer';

export default function PaymentProviderReceiptSection({
  receipt,
  onAttachReceipt,
  onViewReceipt,
  onReplaceReceipt,
  onDeleteReceipt,
  onEditReceipt,
  onViewReceiptFile,
  onDownloadReceiptFile,
  onReplaceReceiptFile,
  onDeleteReceiptFile,
}) {
  const items = Array.isArray(receipt?.items) ? receipt.items : [];

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Recibo del proveedor
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Recibo del pago completo.
          </Typography>
        </Box>
        <PaymentStatusChip value={receipt?.status || 'pending'} />
      </Stack>

      {items.length === 0 ? (
        <Card variant="outlined" sx={{ borderRadius: 1, boxShadow: 'none' }}>
          <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    color: 'warning.main',
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  <ReceiptLong fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Recibo pendiente
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Adjunta el recibo del proveedor.
                  </Typography>
                </Box>
              </Stack>
              {onAttachReceipt && (
                <Button startIcon={<UploadFile fontSize="small" />} variant="outlined" size="small" onClick={onAttachReceipt} sx={{ textTransform: 'none' }}>
                  Adjuntar recibo
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={1}>
          {items.map((item) => (
            <Card key={item.id || item.number} variant="outlined" sx={{ borderRadius: 1, boxShadow: 'none' }}>
              <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
                <Stack spacing={1}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Recibo #{item.number || item.id || 'N/D'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {[item.date ? formatDate(item.date) : null, item.receiptType, item.isPhysical ? 'Fisico' : 'Electronico'].filter(Boolean).join(' - ')}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                        {formatCurrency(item.amount)}
                      </Typography>
                      {onViewReceipt && (
                        <Tooltip title="Ver">
                          <IconButton size="small" onClick={() => onViewReceipt(item)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {onEditReceipt && (
                        <Tooltip title="Editar metadatos">
                          <IconButton size="small" onClick={() => onEditReceipt(item)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {onReplaceReceipt && (
                        <Tooltip title="Reemplazar">
                          <IconButton size="small" onClick={() => onReplaceReceipt(item)}>
                            <UploadFile fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {onDeleteReceipt && (
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => onDeleteReceipt(item)}>
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Stack>

                  {(item.uploadedBy || item.createdAt) && (
                    <Typography variant="caption" color="text.secondary">
                      {[item.uploadedBy ? `Subido por ${item.uploadedBy}` : null, item.createdAt ? `Carga: ${formatDate(item.createdAt)}` : null].filter(Boolean).join(' - ')}
                    </Typography>
                  )}

                  {item.file ? (
                    <PaymentAttachmentPreview
                      attachment={{
                        id: item.id,
                        url: item.file,
                        name: `Recibo ${item.number || item.id || ''}`.trim(),
                        kind: item.file,
                        raw: item,
                      }}
                      onView={onViewReceiptFile}
                      onDownload={onDownloadReceiptFile}
                      onReplace={onReplaceReceiptFile}
                      onDelete={onDeleteReceiptFile}
                    />
                  ) : (
                    <Alert severity="info" sx={{ py: 0.5 }}>
                      Recibo registrado sin archivo adjunto.
                    </Alert>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
